/**
 * Sweeps and reconciliation: the writes nobody asks for by name.
 *
 * LAYER 1, on top of `./store.ts` and `./queues.ts`. These run on app start,
 * after a verdict, or from a dev affordance — they repair state rather than
 * record a user's intent.
 *
 * Import direction inside `src/db/`:
 *   segmentWrites -> maintenance -> queues -> store
 * Nothing here may import `./segmentWrites` (that would close a cycle:
 * `removeSegment` calls `reconcileTask`).
 */

import type { TaskStatus } from '../types';
import { emitDbChange, getDB, getTask, listTasks, updateTask } from './store';
import { areAllSegmentsResolved } from './queues';

/**
 * Delete every segment (and its clip) whose task row no longer exists.
 *
 * Orphans are produced when a slow pipeline stage writes after its task was
 * deleted mid-flight; because the verify/correct queues read cross-task
 * indexes, orphans pollute the badges and the Verify queue and can never be
 * reconciled away (reconcileTask needs a task row). Returns how many segments
 * were removed. Runs once on app start.
 */
export async function deleteOrphanSegments(): Promise<number> {
  const db = await getDB();
  const [taskIds, allSegments] = await Promise.all([
    db.getAllKeys('tasks'),
    db.getAll('segments'),
  ]);
  const alive = new Set(taskIds);
  const orphanIds = allSegments.filter((s) => !alive.has(s.taskId)).map((s) => s.id);
  if (orphanIds.length === 0) return 0;
  const tx = db.transaction(['segments', 'clips'], 'readwrite');
  await Promise.all([
    ...orphanIds.map((sid) => tx.objectStore('segments').delete(sid)),
    ...orphanIds.map((sid) => tx.objectStore('clips').delete(sid)),
  ]);
  await tx.done;
  emitDbChange();
  return orphanIds.length;
}

/* ------------------------------------------------------------------ */
/* Reconciliation                                                      */
/* ------------------------------------------------------------------ */

/**
 * Flip a task into `completed` when all its segments are resolved.
 * Returns the task's status after the check (unchanged if not yet done).
 * Call this after every verify/correct write.
 */
export async function reconcileTask(taskId: string): Promise<TaskStatus | undefined> {
  const task = await getTask(taskId);
  if (!task) return undefined;

  // A task that is mid-pipeline, or whose first pass failed and left the old
  // segments behind, owns its own status. Completing one from here would send a
  // task the user still has to (re-)run straight to the Done tab, transcript and
  // all, off a segment set that is about to be thrown away.
  if (task.status === 'new' || task.status === 'processing') return task.status;

  const done = await areAllSegmentsResolved(taskId);
  if (done && task.status !== 'completed') {
    await updateTask(taskId, { status: 'completed' });
    return 'completed';
  }
  if (!done && task.status === 'completed') {
    // a segment was re-opened — walk the task back to review
    await updateTask(taskId, { status: 'review' });
    return 'review';
  }
  return task.status;
}

/** Run {@link reconcileTask} for every task; returns the ids that completed. */
export async function reconcileAllTasks(): Promise<string[]> {
  const tasks = await listTasks();
  const completed: string[] = [];
  for (const t of tasks) {
    const status = await reconcileTask(t.id);
    if (status === 'completed' && t.status !== 'completed') completed.push(t.id);
  }
  return completed;
}

/* ------------------------------------------------------------------ */
/* Export                                                              */
/* ------------------------------------------------------------------ */
/* Building export payloads lives in `services/reconcile.ts` — this module is
   storage only. See `buildExportPayload` / `buildDataset` there. */

/** Wipe every store. Handy for a dev "reset" affordance. */
export async function clearAll(): Promise<void> {
  const db = await getDB();
  const tx = db.transaction(['tasks', 'segments', 'audio', 'clips', 'logs'], 'readwrite');
  await Promise.all([
    tx.objectStore('tasks').clear(),
    tx.objectStore('segments').clear(),
    tx.objectStore('audio').clear(),
    tx.objectStore('clips').clear(),
    tx.objectStore('logs').clear(),
  ]);
  await tx.done;
  emitDbChange();
  // TODO(E2): call decodedAudio.forget() for every task here — wiping the
  // stores leaves the decoded-audio cache holding a buffer for a task that no
  // longer exists. Cannot be wired from here: db -> services is an upward
  // import; the fix is a change-bus subscription in services. See F11.
}
